import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  EMPLOYEE_ROLES,
  MEDICATION_CATALOGUE_COPY,
  type EmployeeRole,
} from "@care-by-ella/contracts";
import type { StaffMember } from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { listStaff, updateStaff } from "@/lib/staff-api";

// TES-491 — the staff roster. The admin's landing page and the one surface in
// the portal a doctor cannot reach.
//
// Before this the portal could only ADD a doctor: there was no way to see who
// already worked here, no way to make someone a coach, and no way to take
// access away when they left. "Ny läkare" was the whole of staff admin.

const ROLE_LABEL: Record<EmployeeRole, string> = {
  doctor: "Läkare",
  coach: "Coach",
  admin: "Administratör",
};

const ERROR_COPY: Record<string, string> = {
  cannot_deactivate_self:
    "Du kan inte avaktivera ditt eget konto — be en annan administratör göra det.",
  cannot_change_own_role:
    "Du kan inte ändra din egen roll — be en annan administratör göra det.",
  doctor_cannot_hold_grant:
    "En läkare kan inte ha admin-behörighet. Ta bort behörigheten innan rollen ändras till läkare.",
  last_admin:
    "Du är den sista administratören. Ge någon annan admin-behörighet innan du tar bort din egen.",
  staff_not_found: "Personen finns inte längre i kliniken. Ladda om listan.",
  missing_partner_clinic:
    "Din inloggning saknar koppling till en klinik — kontakta support.",
};

function humanError(message: string): string {
  return (
    ERROR_COPY[message] ??
    `Ändringen gick inte igenom (${message}). Försök igen, eller ladda om sidan.`
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE");
}

export function StaffRoute() {
  const { claims } = useAuth();
  const [rows, setRows] = useState<StaffMember[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Per-row error and per-row busy: one colleague's failed role change must
  // not blank the whole roster or freeze the other rows.
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  // Deactivation is a two-step confirm inline in the row rather than a modal:
  // it is reversible (reactivate is right there), so a full dialog would be
  // heavier than the action deserves — but it still must not be one stray click.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await listStaff();
      setRows(res.rows);
    } catch (err) {
      setRows(null);
      setLoadError(err instanceof Error ? err.message : "okänt fel");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function mutate(id: string, body: Parameters<typeof updateStaff>[1]) {
    setBusyId(id);
    setRowError((prev) => {
      const { [id]: _dropped, ...rest } = prev;
      return rest;
    });
    try {
      const updated = await updateStaff(id, body);
      // TES-1089 — revoking her own grant takes this page away from her. A
      // full reload re-reads `/me`, so the nav and the route gate drop the
      // admin sections at once instead of on the next 403.
      // TES-1372 — the same for her own "Insikter" permission, both ways: the
      // menu item comes and goes with it.
      if (
        id === claims?.sub &&
        (body.adminGrant === false ||
          body.insightsGrant !== undefined ||
          body.deviatingMedicationGrant !== undefined)
      ) {
        window.location.assign("/");
        return;
      }
      setRows((prev) =>
        (prev ?? []).map((r) => (r.id === updated.id ? updated : r)),
      );
      setConfirmingId(null);
    } catch (err) {
      setRowError((prev) => ({
        ...prev,
        [id]: humanError(err instanceof Error ? err.message : "okänt fel"),
      }));
    } finally {
      setBusyId(null);
    }
  }

  const heading = (
    <div className="flex items-start justify-between gap-md">
      <div className="flex flex-col gap-xs">
        <h1 className="font-display text-3xl tracking-tight">Personal</h1>
        <p className="text-textSecondary text-sm max-w-xl">
          Alla som kan logga in i Ella Admin för den här kliniken. Rollen
          bestämmer vad de ser — en läkare får den kliniska vyn, en coach sin
          arbetsyta, en administratör den här. En coach med admin-behörighet ser
          båda.
        </p>
      </div>
      <Link
        to="/clinicians/new"
        className="bg-brand text-white px-md py-sm rounded-md font-medium shrink-0"
      >
        Lägg till personal
      </Link>
    </div>
  );

  // Loading: skeleton rows rather than a spinner — the layout is known, and a
  // roster that reflows on arrival reads as a page that broke and recovered.
  if (rows === null && !loadError) {
    return (
      <div className="flex flex-col gap-lg">
        {heading}
        <div className="flex flex-col gap-sm" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-16 rounded-md border border-surfaceMuted bg-surfaceMuted/30 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col gap-lg">
        {heading}
        <div className="border border-danger rounded-md p-md text-sm bg-surface flex flex-col gap-sm items-start">
          <p className="text-danger">
            Personallistan kunde inte hämtas ({loadError}).
          </p>
          <button
            type="button"
            onClick={() => void load()}
            className="px-md py-sm rounded-md border border-surfaceMuted"
          >
            Försök igen
          </button>
        </div>
      </div>
    );
  }

  const staff = rows ?? [];

  return (
    <div className="flex flex-col gap-lg">
      {heading}

      {staff.length === 0 ? (
        // Practically unreachable — the admin reading this page is themselves a
        // row — but a clinic mid-provisioning can hit it, and "teach" beats a
        // bare "0 rader".
        <div className="border border-surfaceMuted rounded-md p-lg bg-surface flex flex-col gap-sm items-start">
          <p className="text-sm font-medium">Ingen personal registrerad ännu</p>
          <p className="text-sm text-textSecondary max-w-lg">
            Lägg till den första läkaren eller coachen. Personen loggar in med
            BankID på sitt personnummer — kontot måste finnas här först.
          </p>
          <Link
            to="/clinicians/new"
            className="bg-brand text-white px-md py-sm rounded-md font-medium"
          >
            Lägg till personal
          </Link>
        </div>
      ) : (
        <ul className="flex flex-col gap-sm">
          {staff.map((member) => {
            const isSelf = member.id === claims?.sub;
            const isDeactivated = Boolean(member.deactivatedAt);
            const busy = busyId === member.id;
            return (
              <li
                key={member.id}
                className={`border border-surfaceMuted rounded-md p-md bg-surface flex flex-col gap-sm ${
                  isDeactivated ? "opacity-60" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-md flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {member.displayName}
                      {isSelf ? (
                        <span className="text-textSecondary font-normal">
                          {" "}
                          — du
                        </span>
                      ) : null}
                    </p>
                    <p className="text-xs text-textSecondary truncate">
                      {member.email} · tillagd {formatDate(member.createdAt)}
                    </p>
                  </div>

                  <div className="flex items-center gap-sm shrink-0">
                    {isDeactivated ? (
                      <span className="text-xs px-sm py-xs rounded-md border border-surfaceMuted">
                        Avaktiverad {formatDate(member.deactivatedAt ?? "")}
                      </span>
                    ) : null}

                    {/* No personal number means BankID has nothing to match, so
                        this person cannot sign in yet — worth saying on the row
                        rather than leaving them to discover it at the login. */}
                    {!member.hasPersonalNumber ? (
                      <span className="text-xs px-sm py-xs rounded-md border border-danger text-danger">
                        Saknar personnummer
                      </span>
                    ) : null}

                    <label className="flex items-center gap-xs text-sm">
                      <span className="sr-only">
                        Roll för {member.displayName}
                      </span>
                      <select
                        value={member.role}
                        disabled={busy || isSelf || isDeactivated}
                        onChange={(e) =>
                          void mutate(member.id, {
                            role: e.target.value as EmployeeRole,
                          })
                        }
                        className="border border-surfaceMuted rounded-md px-sm py-xs bg-surface text-sm disabled:opacity-50"
                      >
                        {EMPLOYEE_ROLES.map((role) => (
                          <option key={role} value={role}>
                            {ROLE_LABEL[role]}
                          </option>
                        ))}
                      </select>
                    </label>

                    {/* TES-1089 — the admin grant, on top of a coach's role.
                        Not offered to a doctor (the API refuses it) nor to an
                        admin, who has it already. */}
                    {member.role === "coach" ? (
                      <label className="flex items-center gap-xs text-sm">
                        <input
                          type="checkbox"
                          checked={member.grantedRoles.includes("admin")}
                          disabled={busy || isDeactivated}
                          onChange={(e) =>
                            void mutate(member.id, {
                              adminGrant: e.target.checked,
                            })
                          }
                          className="disabled:opacity-50"
                        />
                        Admin-behörighet
                      </label>
                    ) : null}

                    {/* TES-1372 — the purpose-bound "Insikter" permission
                        (Ö-546). Any role may hold it; no role implies it. */}
                    <label className="flex items-center gap-xs text-sm">
                      <input
                        type="checkbox"
                        checked={member.permissions.includes("insights")}
                        disabled={busy || isDeactivated}
                        onChange={(e) =>
                          void mutate(member.id, {
                            insightsGrant: e.target.checked,
                          })
                        }
                        className="disabled:opacity-50"
                      />
                      Insikter
                    </label>

                    {/* TES-1400 (Ö-541) — "Annat (avvikande) i
                        läkemedelskatalogen kräver särskild behörighet".
                        Only a doctor orders, so only a doctor is offered it. */}
                    {member.role === "doctor" ? (
                      <label className="flex items-center gap-xs text-sm">
                        <input
                          type="checkbox"
                          checked={member.permissions.includes(
                            "deviating_medication",
                          )}
                          disabled={busy || isDeactivated}
                          onChange={(e) =>
                            void mutate(member.id, {
                              deviatingMedicationGrant: e.target.checked,
                            })
                          }
                          className="disabled:opacity-50"
                        />
                        {MEDICATION_CATALOGUE_COPY.deviation}
                      </label>
                    ) : null}

                    {isDeactivated ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          void mutate(member.id, { deactivated: false })
                        }
                        className="px-md py-xs rounded-md border border-surfaceMuted text-sm disabled:opacity-50"
                      >
                        Återaktivera
                      </button>
                    ) : confirmingId === member.id ? (
                      <span className="flex items-center gap-xs">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            void mutate(member.id, { deactivated: true })
                          }
                          className="px-md py-xs rounded-md bg-danger text-white text-sm disabled:opacity-50"
                        >
                          {busy ? "Avaktiverar…" : "Bekräfta"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmingId(null)}
                          className="px-sm py-xs rounded-md border border-surfaceMuted text-sm"
                        >
                          Avbryt
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        disabled={busy || isSelf}
                        title={
                          isSelf
                            ? "Du kan inte avaktivera ditt eget konto"
                            : undefined
                        }
                        onClick={() => setConfirmingId(member.id)}
                        className="px-md py-xs rounded-md border border-surfaceMuted text-sm disabled:opacity-50"
                      >
                        Avaktivera
                      </button>
                    )}
                  </div>
                </div>

                {confirmingId === member.id ? (
                  <p className="text-xs text-textSecondary">
                    {member.displayName} kan inte längre logga in, varken med
                    BankID eller dev-inloggning. Historiken finns kvar — namnet
                    står kvar på tidigare beslut och recept.
                  </p>
                ) : null}

                {rowError[member.id] ? (
                  <p className="text-xs text-danger">{rowError[member.id]}</p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
